import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getProvisionedMeters,
  createProvisionedMeter,
  updateProvisionedMeter,
  deleteProvisionedMeter,
} from '../api/provisioned-meters-api';
import { useDebounce } from '@/lib/utils';
import type {
  ProvisionedMeter,
  CreateProvisionedMeterRequest,
  UpdateProvisionedMeterRequest,
  PaginatedResponse,
} from '@/types/api';

const PER_PAGE = 15;

export function useDeviceDatabase() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(1, Number(searchParams.get('page') ?? 1));
  const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '');
  const debouncedSearch = useDebounce(searchInput, 300);

  const [result, setResult] = useState<PaginatedResponse<ProvisionedMeter> | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMeter, setEditingMeter] = useState<ProvisionedMeter | null>(null);

  // Delete state
  const [deletingMeter, setDeletingMeter] = useState<ProvisionedMeter | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Reset to page 1 when search changes
  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('page');
      if (debouncedSearch) next.set('q', debouncedSearch);
      else next.delete('q');
      return next;
    }, { replace: true });
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMeters = useCallback(async () => {
    setError(null);
    try {
      const data = await getProvisionedMeters({
        search: debouncedSearch || undefined,
        page: currentPage,
        perPage: PER_PAGE,
      });
      setResult(data);
    } catch (err) {
      console.error('Failed to load provisioned meters:', err);
      setError('Failed to load provisioned meters.');
    } finally {
      setInitialLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    loadMeters();
  }, [loadMeters]);

  function handlePageChange(page: number) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (page > 1) next.set('page', String(page));
      else next.delete('page');
      return next;
    }, { replace: true });
  }

  function handleAdd() {
    setEditingMeter(null);
    setIsModalOpen(true);
  }

  function handleEdit(meter: ProvisionedMeter) {
    setEditingMeter(meter);
    setIsModalOpen(true);
  }

  async function handleSave(data: CreateProvisionedMeterRequest | UpdateProvisionedMeterRequest) {
    if (editingMeter) {
      setIsModalOpen(false);
      try {
        await updateProvisionedMeter(editingMeter.id, data as UpdateProvisionedMeterRequest);
        await loadMeters();
      } catch (err) {
        console.error('Failed to update provisioned meter:', err);
      }
    } else {
      setIsModalOpen(false);
      try {
        await createProvisionedMeter(data as CreateProvisionedMeterRequest);
        await loadMeters();
      } catch (err) {
        console.error('Failed to create provisioned meter:', err);
      }
    }
  }

  async function handleDeleteConfirm() {
    if (!deletingMeter) return;
    const meterToDelete = deletingMeter;
    setIsDeleting(true);
    setDeletingMeter(null);
    try {
      await deleteProvisionedMeter(meterToDelete.id);
      await loadMeters();
    } catch (err) {
      console.error('Failed to delete provisioned meter:', err);
    } finally {
      setIsDeleting(false);
    }
  }

  const meters = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;
  const totalPages = result?.totalPages ?? 1;

  return {
    meters,
    loading: initialLoading,
    error,
    totalCount,
    totalPages,
    currentPage,
    searchInput,
    setSearchInput,
    handlePageChange,
    isModalOpen,
    setIsModalOpen,
    editingMeter,
    handleAdd,
    handleEdit,
    handleSave,
    deletingMeter,
    setDeletingMeter,
    isDeleting,
    handleDeleteConfirm,
  };
}
